import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  anexarImagem,
  fetchAnexos,
  fetchStatusAnexos,
  removerAnexo,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { CotacaoAnexo, CotacaoItem } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import { describeRejectedFiles, prepareMediaFiles } from '../../../utils/mediaFilePreparation';
import ConfirmacaoModal from './ConfirmacaoModal';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Imagens anexadas à cotação, de USO INTERNO — at-11 (28/09/2026).
 *
 * Decisão do Victor, literal: *"O comprador manda, não aparece pro fornecedor"*. Foto da peça,
 * referência visual, o que ajudar quem compra — nada disso vai para o link do fornecedor, e a
 * seção **diz isso** em vez de deixar a dúvida para quem clica em "anexar".
 *
 * ⚠️ **Bootstrap inerte**: sem o armazenamento configurado no ambiente a seção aparece
 * desabilitada com explicação, em vez de oferecer um envio que sempre falharia. É o mesmo
 * desenho do status do assistente (at-87) e do da integração de WhatsApp (at-07).
 */

/**
 * Teto do anexo, alinhado ao `FileInterceptor` do backend.
 *
 * ⚠️ **NÃO é o `MAX_IMAGE_BYTES` (30 MB) do preparo de mídia**: aquele existe por causa do teto
 * de request do Cloud Run no checklist. Aqui o limite é menor de propósito, e barrar na tela é o
 * que transforma "413 sem mensagem" em uma frase que a pessoa entende.
 */
const MAX_ANEXO_BYTES = 10 * 1024 * 1024;

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

interface AnexosSectionProps {
  cotacaoId: string;
  /** Itens da cotação, para o vínculo opcional da imagem com uma peça. */
  itens: CotacaoItem[];
  /**
   * Quem pode anexar e remover. O TI enxerga a cotação para dar suporte, mas não opera
   * (ADR 0012) — e o backend recusa igualmente; aqui os controles nem aparecem.
   */
  podeEditar: boolean;
}

const AnexosSection: React.FC<AnexosSectionProps> = ({ cotacaoId, itens, podeEditar }) => {
  const [anexos, setAnexos] = useState<CotacaoAnexo[]>([]);
  const [configurado, setConfigurado] = useState<boolean | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [itemEscolhido, setItemEscolhido] = useState('');
  const [aRemover, setARemover] = useState<CotacaoAnexo | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // Em paralelo: o status é uma pergunta sobre o ambiente e não depende da cotação.
      const [status, lista] = await Promise.all([fetchStatusAnexos(), fetchAnexos(cotacaoId)]);
      setConfigurado(status.configurado);
      setAnexos(lista);
      setErro(false);
    } catch (error) {
      /*
       * 🔴 Lista vazia por erro seria lida como "esta cotação não tem imagem" — e o comprador
       * anexaria de novo a mesma foto, ou concluiria que a referência que ele mandou se perdeu.
       * A seção declara a falha e NÃO diz "nenhuma imagem" (regra da at-60).
       */
      setAnexos([]);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as imagens desta cotação. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [cotacaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function escolherArquivos(e: React.ChangeEvent<HTMLInputElement>) {
    const escolhidos = Array.from(e.target.files ?? []);
    // Limpa o input antes de qualquer await: sem isso, escolher o MESMO arquivo de novo não
    // dispara `change` e a tela parece travada.
    if (inputRef.current) inputRef.current.value = '';
    if (escolhidos.length === 0) return;

    setEnviando(true);
    try {
      /*
       * Reusa o preparo de mídia do checklist (at-29): copia para a memória (imune a arquivo
       * que some do disco), converte HEIC do iPhone para JPEG e recusa o que não é imagem.
       * `allowPdf` e `allowVideo` ficam desligados — aqui é foto de peça.
       */
      const { ready, rejected } = await prepareMediaFiles(escolhidos, {
        allowVideo: false,
        allowPdf: false,
      });
      if (rejected.length > 0) notify.warning(describeRejectedFiles(rejected));

      const grandes = ready.filter((f) => f.size > MAX_ANEXO_BYTES);
      if (grandes.length > 0) {
        notify.warning(
          `${grandes.length === 1 ? 'Uma imagem passa' : `${grandes.length} imagens passam`} de ` +
            `${tamanhoLegivel(MAX_ANEXO_BYTES)} e não ${grandes.length === 1 ? 'foi enviada' : 'foram enviadas'}.`,
        );
      }

      const enviaveis = ready.filter((f) => f.size <= MAX_ANEXO_BYTES);
      let enviadas = 0;
      for (const arquivo of enviaveis) {
        try {
          await anexarImagem(cotacaoId, arquivo, itemEscolhido || undefined);
          enviadas += 1;
        } catch (error) {
          // Uma falha não interrompe as demais, e cada uma é nomeada: enviar 5 e dizer só
          // "erro" deixaria o comprador sem saber qual reenviar.
          notify.errorFrom(error, `Não foi possível anexar "${arquivo.name}".`);
        }
      }

      if (enviadas > 0) {
        notify.success(enviadas === 1 ? 'Imagem anexada.' : `${enviadas} imagens anexadas.`);
        await carregar();
      }
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível preparar as imagens escolhidas.');
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarRemocao() {
    if (!aRemover) return;
    setEnviando(true);
    try {
      await removerAnexo(aRemover.id);
      notify.success('Imagem removida.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível remover a imagem.');
    } finally {
      setEnviando(false);
      setARemover(null);
    }
  }

  return (
    <div className="pc-card">
      <h2 className="pc-card-titulo">Imagens (uso interno)</h2>
      <div className="pc-card-corpo">
        <p className="pc-hint" style={{ marginTop: 0 }}>
          Fotos da peça ou referências para quem compra. <strong>O fornecedor não vê</strong> — nada disto vai
          para o link dele.
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar as imagens desta cotação. Pode haver imagens anexadas que não estão
            aparecendo aqui — recarregue a página antes de anexar de novo.
          </p>
        ) : carregando ? (
          <p className="pc-texto-secundario">Carregando...</p>
        ) : (
          <>
            {configurado === false && (
              <p className="pc-hint">
                O armazenamento de imagens não está configurado neste ambiente, então não é possível anexar.
                Acione a TI.
              </p>
            )}

            {podeEditar && configurado && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {itens.length > 0 && (
                  <select
                    className="premium-text-input"
                    aria-label="Peça a que a imagem se refere"
                    value={itemEscolhido}
                    disabled={enviando}
                    onChange={(e) => setItemEscolhido(e.target.value)}
                  >
                    <option value="">Imagem da cotação (sem peça específica)</option>
                    {itens.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.descricao}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className="pc-btn pc-btn--secundario pc-btn--sm"
                  disabled={enviando}
                  onClick={() => inputRef.current?.click()}
                >
                  {enviando ? 'Enviando...' : 'Anexar imagens'}
                </button>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={escolherArquivos}
                />
              </div>
            )}

            {anexos.length === 0 ? (
              <p className="pc-texto-secundario" style={{ marginTop: 12 }}>
                Nenhuma imagem anexada.
              </p>
            ) : (
              <div className="pc-anexos-grade">
                {anexos.map((a) => (
                  <div key={a.id} className="pc-anexo">
                    {a.url ? (
                      <img
                        className="pc-anexo-miniatura"
                        src={a.url}
                        alt={a.nomeOriginal}
                        title="Abrir em nova aba"
                        onClick={() => window.open(a.url!, '_blank', 'noopener')}
                      />
                    ) : (
                      // O anexo continua na lista: um que existe e não aparece faria o
                      // comprador reenviar a mesma imagem.
                      <div className="pc-anexo-sem-previa">Prévia indisponível agora</div>
                    )}
                    <div className="pc-anexo-info">
                      <span className="pc-anexo-nome" title={a.nomeOriginal}>
                        {a.nomeOriginal}
                      </span>
                      <span className="pc-anexo-meta" title={a.itemDescricao ?? undefined}>
                        {a.itemDescricao ?? 'Cotação'} · {tamanhoLegivel(a.tamanhoBytes)}
                      </span>
                      <span className="pc-anexo-meta">
                        {a.enviadoPor ?? 'autor não registrado'} ·{' '}
                        {new Date(a.enviadoEm).toLocaleDateString('pt-BR')}
                      </span>
                      {podeEditar && (
                        <button
                          type="button"
                          className="pc-anexo-remover"
                          disabled={enviando}
                          onClick={() => setARemover(a)}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmacaoModal
        isOpen={aRemover !== null}
        titulo="Remover esta imagem?"
        descricao={
          <>
            <p style={{ margin: '0 0 10px' }}>
              <strong>{aRemover?.nomeOriginal}</strong> sai desta tela. O registro de que a imagem existiu, e de
              quem a removeu, fica na auditoria.
            </p>
          </>
        }
        rotuloConfirmar="Remover"
        carregando={enviando}
        onConfirmar={confirmarRemocao}
        onCancelar={() => setARemover(null)}
      />
    </div>
  );
};

export default AnexosSection;
